import React, { useRef } from 'react'
import Select from 'react-select'
import { CAROUSEL_SIZE_OPTIONS, isCarouselWidgetType, widgetTypeHasFormTitle } from '../../constants/widgetTypes'
import { WidgetFormState } from '../../hooks'
import formStyles from '../../styles/forms.module.scss'
import selectStyles from '../../styles/react-select.module.scss'
import { Badge } from '../Badge'
import EditorSection from './EditorSection'
import WidgetMetadataEditor from './metadata-editors'
import type { CarouselMetadata } from './metadata-editors/CarouselEditor'
import CarouselRailSettings from './metadata-editors/CarouselRailSettings'
import WidgetTypeSelector from './WidgetTypeSelector'

// ACE's per-response ceiling (widgetPageMaxLimit); the grid has no paging yet.
const GRID_MAX_LIMIT = 100

const SIZE_OPTIONS = CAROUSEL_SIZE_OPTIONS.map(o => ({ value: o.code, label: o.label }))

interface WidgetFormProps {
  state: WidgetFormState
  namePlaceholder?: string
}

const WidgetForm: React.FC<WidgetFormProps> = ({ state, namePlaceholder }) => {
  const { name, setName, type, setType, metadata, setMetadata, widgetId, autoCreating, typeLocked, handleNameBlur, touchProps } = state

  const hasFormTitle = widgetTypeHasFormTitle(type)
  const isCarousel = isCarouselWidgetType(type)
  const isGrid = type === 'standard_landscape_grid'
  const title = typeof metadata.title === 'string' ? metadata.title : ''

  // Name and Title mirror each other until the user takes ownership of a field
  // (edits it, or the widget loaded with a value already set); whichever field
  // is typed into first stops receiving the mirror and starts driving it.
  // Parents reuse one WidgetForm instance across widgets via formState.reset()
  // rather than remounting it, so re-evaluate the links whenever a different
  // widget loads in (widgetId change).
  const titleLinked = useRef(title === '')
  const nameLinked = useRef(name === '')
  const syncedWidgetId = useRef(widgetId)
  if (syncedWidgetId.current !== widgetId) {
    syncedWidgetId.current = widgetId
    titleLinked.current = title === ''
    nameLinked.current = name === ''
  }

  const handleNameChange = (v: string) => {
    nameLinked.current = false
    setName(v)
    if (hasFormTitle && titleLinked.current) setMetadata({ ...metadata, title: v })
  }

  const handleTitleChange = (v: string) => {
    titleLinked.current = false
    setMetadata({ ...metadata, title: v })
    if (nameLinked.current) setName(v)
  }

  const handleGridLimitChange = (raw: string) => {
    const next = { ...metadata }
    const n = parseInt(raw, 10)
    if (Number.isNaN(n)) delete next.limit
    else next.limit = Math.min(Math.max(n, 1), GRID_MAX_LIMIT)
    setMetadata(next)
  }

  const nameField = (
    <div className={formStyles.fieldGroup}>
      <label>Name <Badge variant="neutral" size="small">Internal</Badge></label>
      <input
        type="text"
        value={name}
        onChange={(e) => handleNameChange(e.target.value)}
        onBlur={() => {
          void handleNameBlur()
        }}
        placeholder={namePlaceholder ?? 'e.g., US Open Hero Banner'}
        disabled={autoCreating}
      />
    </div>
  )

  // Capture phase, so an interaction anywhere in the form — including inside
  // the per-type metadata editors — establishes the unsaved-changes baseline.
  return (
    <div className={formStyles.formField} {...touchProps}>
      {!typeLocked && (
        <div className={formStyles.fieldGroup}>
          <label>Widget Type</label>
          <WidgetTypeSelector
            value={type}
            onChange={(code) => {
              // Re-picking the "Carousel" card while already on a carousel would
              // reset the size to the default and wipe the shared config — skip it.
              if (isCarouselWidgetType(code) && isCarouselWidgetType(type)) return
              setType(code)
              // A new carousel starts with its "see more" link on, shown only
              // when the collection holds more than the rail displays.
              setMetadata(isCarouselWidgetType(code) ? { show_more: { mode: 'if_more' } } : {})
            }}
          />
        </div>
      )}

      {type && (
        <>
          {isCarousel || isGrid ? (
            // Identity and presentation: what the widget is called, what
            // viewers see, and which card shape it renders as. Size lives here
            // rather than in the metadata editor because it is the widget_type
            // itself, not a metadata field.
            <EditorSection title="Basics">
              <div className={formStyles.formGrid}>
                {nameField}
                <div className={formStyles.fieldGroup}>
                  <label>Title <Badge variant="secondary" size="small">Public</Badge></label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="e.g., Live & Upcoming"
                  />
                </div>
                {isCarousel ? (
                  <div className={formStyles.fieldGroup}>
                    <label>Size &amp; orientation</label>
                    <Select
                      value={SIZE_OPTIONS.find(o => o.value === type) ?? null}
                      onChange={(opt) => opt && setType(opt.value)}
                      options={SIZE_OPTIONS}
                      className={selectStyles.reactSelect}
                      classNamePrefix="react-select"
                    />
                  </div>
                ) : (
                  <div className={formStyles.fieldGroup}>
                    <label title="How many cards the grid shows, pins included.">Limit</label>
                    <input
                      type="number"
                      min={1}
                      max={GRID_MAX_LIMIT}
                      placeholder="25"
                      value={typeof metadata.limit === 'number' ? metadata.limit : ''}
                      onChange={(e) => handleGridLimitChange(e.target.value)}
                    />
                  </div>
                )}
              </div>
              {isCarousel && (
                <CarouselRailSettings
                  metadata={metadata as CarouselMetadata}
                  onChange={(m) => setMetadata(m)}
                />
              )}
            </EditorSection>
          ) : hasFormTitle ? (
            <div className={formStyles.formGrid}>
              {nameField}
              <div className={formStyles.fieldGroup}>
                <label>Title <Badge variant="secondary" size="small">Public</Badge></label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="e.g., Live & Upcoming"
                />
              </div>
            </div>
          ) : (
            nameField
          )}

          <WidgetMetadataEditor
            widgetType={type}
            metadata={metadata as Record<string, unknown>}
            onChange={(m) => setMetadata(m)}
            widgetId={widgetId ?? undefined}
          />
        </>
      )}
    </div>
  )
}

export default WidgetForm
